import {
  Alert,
  Autocomplete,
  Box,
  Button,
  FormControlLabel,
  Grid,
  Paper,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import DialogComponent from "../../../common/components/functional/dialogs/DialogComponent";
import useMessage from "../../../hooks/useMessage";
import useTesAutocompleteQuery from "../../../hooks/useTesAutocompleteQuery";
import {
  TesSalaryTableDocument,
  TesSalaryTablePublishCandidateSelectionCountByType,
  TesSalaryTablePublishCommandResult,
  countEffectiveSalaries,
} from "../../tesSalaryTables/tesSalaryTablesApi";
import {
  archiveTesSalaryTableThunk,
  copyTesSalaryTableToDraftThunk,
  createTesSalaryTableDraftThunk,
  deleteTesSalaryTableDraftThunk,
  fetchTesSalaryTableByIdThunk,
  estimateTesSalaryTablePublishThunk,
  publishOrContinueTesSalaryTableThunk,
  replaceTesSalaryTableDraftThunk,
  setTesSalaryTableConfigThunk,
  selectTesSalaryTableConfigByTes,
} from "../../tesSalaryTables/tesSalaryTablesSlice";
import TesSalaryTableEditorDialog from "./TesSalaryTableEditorDialog";
import TesSalaryTablePublishDialog from "./TesSalaryTablePublishDialog";
import TesSalaryTableSeriesCard from "./TesSalaryTableSeriesCard";
import TesSalaryTableSeriesDialog from "./TesSalaryTableSeriesDialog";
import { sanitizeDraftContent } from "./tesSalaryTableDraftSanitizers";
import {
  FilterMode,
  hasDuplicateSeriesNameAndEffectiveDate,
  useTesSalaryTablesAdminData,
} from "./useTesSalaryTablesAdminData";

const PUBLISH_MAX_ATTEMPTS_PER_RUN = 200;
const PUBLISH_SKIPPED_PAUSE_THRESHOLD = 5;
const PUBLISH_PROJECTION_POLL_INTERVAL_MS = 1500;
const PUBLISH_PROJECTION_FIRST_POLL_MIN_MS = 1000;
const PUBLISH_PROJECTION_FIRST_POLL_MAX_MS = 2000;
const PUBLISH_PROJECTION_COUNT_TOLERANCE = 1;

const EFFECTIVE_SALARY_SOURCE_TYPE_BY_PUBLISH_SOURCE = {
  EMPLOYMENT_ATTRIBUTES: "SHIFT_EMPLOYMENT_ATTRIBUTES",
  SHIFT_EMPLOYMENT: "SHIFT_EMPLOYMENT_SALARY",
  EMPLOYMENT: "EMPLOYMENT_SALARY",
} as const;

type PublishSourceSelection = {
  EMPLOYMENT_ATTRIBUTES: boolean;
  SHIFT_EMPLOYMENT: boolean;
  EMPLOYMENT: boolean;
};

type ProjectionCatchUpStatus = {
  completed: number;
  target: number;
  ready: boolean;
};

const buildEffectiveSalarySourceTypesParam = (
  selection: PublishSourceSelection,
): string | undefined => {
  const sourceTypes = (
    Object.entries(selection) as Array<[keyof PublishSourceSelection, boolean]>
  )
    .filter(([, selected]) => selected)
    .map(([source]) => EFFECTIVE_SALARY_SOURCE_TYPE_BY_PUBLISH_SOURCE[source]);
  return sourceTypes.length > 0 ? sourceTypes.join(",") : undefined;
};

const sumAlreadyTargetTesSalaryTableCount = (
  counts: TesSalaryTablePublishCandidateSelectionCountByType | null,
  selection: PublishSourceSelection,
): number => {
  if (!counts) return 0;
  return (
    (selection.EMPLOYMENT_ATTRIBUTES
      ? counts.employmentAttributes.alreadyTargetTesSalaryTableId
      : 0) +
    (selection.SHIFT_EMPLOYMENT
      ? counts.shiftEmployments.alreadyTargetTesSalaryTableId
      : 0) +
    (selection.EMPLOYMENT
      ? counts.employments.alreadyTargetTesSalaryTableId
      : 0)
  );
};

const TesSalaryTablesControlPanel = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { sendMessage } = useMessage();
  const { tes } = useAppSelector((state) => state.localized);
  const localizedWorkTitles = useAppSelector(
    (state) => state.localized.workTitle,
  );
  const { setTesSearchTerm, tesIdsQueryResult } = useTesAutocompleteQuery();

  const [tesId, setTesId] = useState<string | null>(null);
  const [editorMode, setEditorMode] = useState<
    "create" | "edit" | "copy" | "view"
  >("create");
  const [editorOpen, setEditorOpen] = useState(false);
  const [selectedTable, setSelectedTable] =
    useState<TesSalaryTableDocument | null>(null);
  const [tableToDelete, setTableToDelete] =
    useState<TesSalaryTableDocument | null>(null);
  const [tableToArchive, setTableToArchive] =
    useState<TesSalaryTableDocument | null>(null);
  const [tableToPublish, setTableToPublish] =
    useState<TesSalaryTableDocument | null>(null);
  const [publishingTableId, setPublishingTableId] = useState<string | null>(
    null,
  );
  const [publishProcessedTotal, setPublishProcessedTotal] = useState(0);
  const [publishSkippedTotal, setPublishSkippedTotal] = useState(0);
  const [publishEstimatedTotal, setPublishEstimatedTotal] = useState<
    number | null
  >(null);
  const [publishEstimatedByType, setPublishEstimatedByType] =
    useState<TesSalaryTablePublishCandidateSelectionCountByType | null>(null);
  const [publishSourceSelection, setPublishSourceSelection] = useState({
    EMPLOYMENT_ATTRIBUTES: true,
    SHIFT_EMPLOYMENT: true,
    EMPLOYMENT: true,
  });
  const [publishCursor, setPublishCursor] = useState<string | null>(null);
  const [publishVersion, setPublishVersion] = useState<number | null>(null);
  const [publishPausedReason, setPublishPausedReason] = useState<string | null>(
    null,
  );
  const [publishWaitingMs, setPublishWaitingMs] = useState<number | null>(null);
  const [publishProjectionCatchUp, setPublishProjectionCatchUp] =
    useState<ProjectionCatchUpStatus | null>(null);
  const [isPublishValidating, setIsPublishValidating] = useState(false);
  const [backendGap, setBackendGap] = useState<string | null>(null);
  const [isSavingConfig, setIsSavingConfig] = useState(false);
  const [selectedSeriesId, setSelectedSeriesId] = useState<string | null>(null);
  const config = useAppSelector((state) =>
    selectTesSalaryTableConfigByTes(state, tesId),
  );
  const [enabled, setEnabled] = useState(false);
  const publishPauseRequestedRef = useRef(false);
  const publishContinueNowRef = useRef<(() => void) | null>(null);
  const publishAlreadyTargetBaselineRef = useRef<number | null>(null);
  const {
    groupedTables,
    filteredGroups,
    filterMode,
    setFilterMode,
    reload,
    isSeriesDialogOpen,
    setIsSeriesDialogOpen,
    newSeriesName,
    setNewSeriesName,
    newSeriesDescription,
    setNewSeriesDescription,
    isCreatingSeries,
    closeCreateSeriesDialog,
    submitCreateSeries,
    seriesToEdit,
    editedSeriesName,
    setEditedSeriesName,
    editedSeriesDescription,
    setEditedSeriesDescription,
    isSavingSeriesEdit,
    openSeriesEditDialog,
    closeSeriesEditDialog,
    submitSeriesEdit,
  } = useTesSalaryTablesAdminData(tesId);

  const workTitleOptions = useMemo(
    () =>
      Object.entries(localizedWorkTitles || {}).map(([id, name]) => ({
        id,
        name,
      })),
    [localizedWorkTitles],
  );

  useEffect(() => {
    if (config) {
      setEnabled(config.enabled);
    }
  }, [config]);

  const openEditor = async (
    mode: "create" | "edit" | "copy" | "view",
    table?: TesSalaryTableDocument,
    seriesId?: string,
  ) => {
    setBackendGap(null);
    setEditorMode(mode);

    if (!table) {
      setSelectedSeriesId(seriesId || null);
      setSelectedTable(null);
      setEditorOpen(true);
      return;
    }

    const loadedTable = await dispatch(
      fetchTesSalaryTableByIdThunk(table.tesSalaryTableId),
    ).unwrap();
    setSelectedTable(loadedTable);

    if (
      loadedTable?.cells.some((cell) => !!cell.monthlySalary) &&
      !loadedTable.monthlyDivider
    ) {
      console.log("monthly divider gap", loadedTable?.cells);
      setBackendGap(t("settings:tesSalaryTablesMonthlyDividerQueryGap"));
    }

    setEditorOpen(true);
  };

  const waitBeforeNextPublishPass = (
    ms: number,
    getProjectionCatchUp?: () => Promise<ProjectionCatchUpStatus | null>,
  ) =>
    new Promise<void>((resolve) => {
      if (ms <= 0) {
        resolve();
        return;
      }
      const waitsForProjectionCatchUp = !!getProjectionCatchUp;
      setPublishWaitingMs(ms);
      setPublishProjectionCatchUp(null);
      const startedAt = Date.now();
      let timeoutId: ReturnType<typeof setTimeout> | null = null;
      let pollTimeoutId: ReturnType<typeof setTimeout> | null = null;
      let intervalId: ReturnType<typeof setInterval> | null = null;
      let finished = false;
      let estimatedReadyAt = Date.now() + ms;
      const finish = () => {
        if (finished) return;
        finished = true;
        if (timeoutId) clearTimeout(timeoutId);
        if (pollTimeoutId) clearTimeout(pollTimeoutId);
        if (intervalId) clearInterval(intervalId);
        publishContinueNowRef.current = null;
        setPublishWaitingMs(null);
        setPublishProjectionCatchUp(null);
        resolve();
      };
      const updateEstimatedWait = (estimatedMs: number) => {
        const nextEstimatedMs = Math.max(
          PUBLISH_PROJECTION_POLL_INTERVAL_MS,
          Math.round(estimatedMs),
        );
        estimatedReadyAt = Date.now() + nextEstimatedMs;
        setPublishWaitingMs(nextEstimatedMs);
      };
      const pollProjectionCatchUp = async () => {
        if (finished || !getProjectionCatchUp) return;
        const catchUp = await getProjectionCatchUp();
        if (finished) return;
        if (catchUp) {
          setPublishProjectionCatchUp(catchUp);
          if (catchUp.ready) {
            finish();
            return;
          }
          const elapsedMs = Math.max(1, Date.now() - startedAt);
          if (catchUp.completed > 0 && catchUp.target > catchUp.completed) {
            const completedPerMs = catchUp.completed / elapsedMs;
            updateEstimatedWait(
              (catchUp.target - catchUp.completed) / completedPerMs,
            );
          } else {
            updateEstimatedWait(Math.max(ms - elapsedMs, ms / 2));
          }
        }
        pollTimeoutId = setTimeout(
          pollProjectionCatchUp,
          PUBLISH_PROJECTION_POLL_INTERVAL_MS,
        );
      };
      publishContinueNowRef.current = finish;
      intervalId = setInterval(() => {
        const remaining = Math.max(0, estimatedReadyAt - Date.now());
        setPublishWaitingMs(
          waitsForProjectionCatchUp ? Math.max(1000, remaining) : remaining,
        );
      }, 250);
      if (waitsForProjectionCatchUp) {
        const firstPollDelay = Math.min(
          PUBLISH_PROJECTION_FIRST_POLL_MAX_MS,
          Math.max(PUBLISH_PROJECTION_FIRST_POLL_MIN_MS, ms / 4),
        );
        pollTimeoutId = setTimeout(pollProjectionCatchUp, firstPollDelay);
      } else {
        timeoutId = setTimeout(finish, ms);
      }
    });

  const getReadableTableLabel = (
    name?: string | null,
    effectiveDate?: string | null,
  ) => {
    if (!name) return null;
    return effectiveDate ? `${name} (${effectiveDate})` : name;
  };

  const getProcessedThisCall = (response: unknown) => {
    if (!response || Array.isArray(response) || typeof response !== "object") {
      return 0;
    }
    const maybeProgress =
      response as Partial<TesSalaryTablePublishCommandResult>;
    return typeof maybeProgress.processedThisCall === "number"
      ? maybeProgress.processedThisCall
      : 0;
  };

  const getSkippedThisCall = (response: unknown) => {
    if (!response || Array.isArray(response) || typeof response !== "object") {
      return 0;
    }
    const maybeProgress =
      response as Partial<TesSalaryTablePublishCommandResult>;
    return typeof maybeProgress.skippedThisCall === "number"
      ? maybeProgress.skippedThisCall
      : 0;
  };

  const getSuggestedDelayMs = (response: unknown) => {
    if (!response || Array.isArray(response) || typeof response !== "object") {
      return 250;
    }
    const maybeProgress =
      response as Partial<TesSalaryTablePublishCommandResult>;
    return typeof maybeProgress.suggestedDelayMs === "number" &&
      maybeProgress.suggestedDelayMs > 0
      ? maybeProgress.suggestedDelayMs
      : 250;
  };

  const getEventsCreatedThisCall = (response: unknown) => {
    if (!response || Array.isArray(response) || typeof response !== "object") {
      return 0;
    }
    const maybeProgress =
      response as Partial<TesSalaryTablePublishCommandResult>;
    return typeof maybeProgress.eventsCreatedThisCall === "number"
      ? maybeProgress.eventsCreatedThisCall
      : 0;
  };

  const countTargetLinkedEffectiveSalaries = async (
    table: TesSalaryTableDocument,
    selection: PublishSourceSelection,
  ): Promise<number | null> => {
    const sourceTypes = buildEffectiveSalarySourceTypesParam(selection);
    if (!table.tesId || !table.effectiveDate || !sourceTypes) {
      return null;
    }
    try {
      const result = await countEffectiveSalaries({
        tesId: table.tesId,
        date: table.effectiveDate,
        tesSalaryTableId: table.tesSalaryTableId,
        sourceTypes,
      });
      return result.count;
    } catch {
      return null;
    }
  };

  const refreshPublishAlreadyTargetBaseline = async (
    table: TesSalaryTableDocument,
    selection: PublishSourceSelection,
  ) => {
    const count = await countTargetLinkedEffectiveSalaries(table, selection);
    if (count != null) {
      publishAlreadyTargetBaselineRef.current = count;
    }
  };

  const getSelectedPublishEstimate = (
    counts: TesSalaryTablePublishCandidateSelectionCountByType | null,
    selection: typeof publishSourceSelection,
  ) => {
    if (!counts) return publishEstimatedTotal;
    return (
      (selection.EMPLOYMENT_ATTRIBUTES
        ? counts.employmentAttributes.matching
        : 0) +
      (selection.SHIFT_EMPLOYMENT ? counts.shiftEmployments.matching : 0) +
      (selection.EMPLOYMENT ? counts.employments.matching : 0)
    );
  };

  const normalizePublishSourceSelection = (
    counts: TesSalaryTablePublishCandidateSelectionCountByType | null,
    current: typeof publishSourceSelection,
  ) => ({
    EMPLOYMENT_ATTRIBUTES:
      (counts?.employmentAttributes.matching ?? 1) > 0 &&
      current.EMPLOYMENT_ATTRIBUTES,
    SHIFT_EMPLOYMENT:
      (counts?.shiftEmployments.matching ?? 1) > 0 && current.SHIFT_EMPLOYMENT,
    EMPLOYMENT: (counts?.employments.matching ?? 1) > 0 && current.EMPLOYMENT,
  });

  const confirmPublish = async () => {
    if (!tableToPublish || publishingTableId) return;
    const targetTableId = tableToPublish.tesSalaryTableId;
    publishPauseRequestedRef.current = false;
    setPublishPausedReason(null);
    setPublishingTableId(targetTableId);
    const selectedSources = Object.entries(publishSourceSelection)
      .filter(([, selected]) => selected)
      .map(([source]) => source) as Array<
      "EMPLOYMENT_ATTRIBUTES" | "SHIFT_EMPLOYMENT" | "EMPLOYMENT"
    >;
    const includeSources =
      selectedSources.length === 3 ? undefined : selectedSources;
    try {
      let version = publishVersion ?? tableToPublish.version;
      let isPublished = false;
      let cursor = publishCursor;
      for (
        let attempt = 0;
        attempt < PUBLISH_MAX_ATTEMPTS_PER_RUN;
        attempt += 1
      ) {
        if (publishPauseRequestedRef.current) {
          setPublishPausedReason(
            t("settings:tesSalaryTablesPublishPausedByUser"),
          );
          break;
        }
        const result: TesSalaryTablePublishCommandResult = await dispatch(
          publishOrContinueTesSalaryTableThunk({
            tesSalaryTableId: targetTableId,
            version,
            batchSize: 500,
            includeSources,
            cursor,
          }),
        ).unwrap();
        const processedThisCall = getProcessedThisCall(result);
        const skippedThisCall = getSkippedThisCall(result);
        setPublishProcessedTotal((current) => current + processedThisCall);
        setPublishSkippedTotal((current) => current + skippedThisCall);
        cursor = result.cursor ?? null;
        setPublishCursor(cursor);

        const latest = await dispatch(
          fetchTesSalaryTableByIdThunk(targetTableId),
        ).unwrap();
        if (latest?.version !== undefined) {
          version = latest.version;
          setPublishVersion(latest.version);
        }
        if (latest?.status === "PUBLISHED" || result.status === "PUBLISHED") {
          sendMessage(t("settings:tesSalaryTablesPublishDone"), "success");
          isPublished = true;
          break;
        }
        if (skippedThisCall > PUBLISH_SKIPPED_PAUSE_THRESHOLD) {
          setPublishPausedReason(
            t("settings:tesSalaryTablesPublishPausedSkipped", {
              count: skippedThisCall,
            }),
          );
          break;
        }
        const eventsCreatedThisCall = getEventsCreatedThisCall(result);
        const shouldPollProjectionCatchUp =
          eventsCreatedThisCall > 0 && !!tableToPublish.sourceTableId;
        await waitBeforeNextPublishPass(
          getSuggestedDelayMs(result),
          shouldPollProjectionCatchUp
            ? async () => {
                const baseline = publishAlreadyTargetBaselineRef.current;
                if (baseline == null) return null;
                const count = await countTargetLinkedEffectiveSalaries(
                  tableToPublish,
                  publishSourceSelection,
                );
                if (count == null) return null;

                const target = Math.max(
                  0,
                  eventsCreatedThisCall - PUBLISH_PROJECTION_COUNT_TOLERANCE,
                );
                const completed = Math.max(0, count - baseline);
                return {
                  completed: Math.min(completed, target),
                  target,
                  ready: completed >= target,
                };
              }
            : undefined,
        );
        if (shouldPollProjectionCatchUp) {
          await refreshPublishAlreadyTargetBaseline(
            tableToPublish,
            publishSourceSelection,
          );
        }
      }
      if (!isPublished) {
        setPublishPausedReason(
          (current) =>
            current || t("settings:tesSalaryTablesPublishPausedAttemptLimit"),
        );
        setPublishWaitingMs(null);
        setPublishProjectionCatchUp(null);
        sendMessage(t("settings:tesSalaryTablesPublishInProgress"), "warning");
      }
      await reload();
      if (isPublished) {
        setTableToPublish(null);
        setPublishProcessedTotal(0);
        setPublishSkippedTotal(0);
        setPublishCursor(null);
        setPublishVersion(null);
        setPublishPausedReason(null);
        setPublishWaitingMs(null);
        setPublishProjectionCatchUp(null);
      }
    } finally {
      setPublishingTableId(null);
    }
  };

  useEffect(() => {
    if (!tableToPublish || publishingTableId) return;

    setPublishEstimatedTotal(null);
    setPublishEstimatedByType(null);
    if (!tableToPublish.sourceTableId) {
      setIsPublishValidating(false);
      return;
    }

    setIsPublishValidating(true);
    dispatch(
      estimateTesSalaryTablePublishThunk({
        tesSalaryTableId: tableToPublish.tesSalaryTableId,
      }),
    )
      .unwrap()
      .then((result) => {
        setPublishEstimatedTotal(result.estimatedTotalCandidates);
        const nextCounts = result.estimatedCandidates.byType || null;
        setPublishEstimatedByType(nextCounts);
        setPublishSourceSelection((current) => {
          const normalizedSelection = normalizePublishSourceSelection(
            nextCounts,
            current,
          );
          publishAlreadyTargetBaselineRef.current =
            sumAlreadyTargetTesSalaryTableCount(
              nextCounts,
              normalizedSelection,
            );
          return normalizedSelection;
        });
      })
      .catch(() => {
        setPublishEstimatedTotal(null);
        setPublishEstimatedByType(null);
      })
      .finally(() => {
        setIsPublishValidating(false);
      });
  }, [dispatch, publishingTableId, tableToPublish]);

  const publishSelectedEstimatedTotal = getSelectedPublishEstimate(
    publishEstimatedByType,
    publishSourceSelection,
  );

  const publishProgressPercent =
    publishSelectedEstimatedTotal && publishSelectedEstimatedTotal > 0
      ? Math.min(
          100,
          Math.round(
            (publishProcessedTotal / publishSelectedEstimatedTotal) * 100,
          ),
        )
      : null;

  const preparePublish = async (table: TesSalaryTableDocument) => {
    setPublishSourceSelection({
      EMPLOYMENT_ATTRIBUTES: true,
      SHIFT_EMPLOYMENT: true,
      EMPLOYMENT: true,
    });
    setPublishProcessedTotal(0);
    setPublishSkippedTotal(0);
    setPublishCursor(null);
    setPublishVersion(table.version);
    setPublishPausedReason(null);
    publishPauseRequestedRef.current = false;
    publishContinueNowRef.current = null;
    publishAlreadyTargetBaselineRef.current = null;
    setPublishWaitingMs(null);
    setPublishProjectionCatchUp(null);
    setTableToPublish(table);
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} marginBottom={2}>
        {t("settings:tesSalaryTablesTitle")}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Autocomplete
            value={tesId}
            options={
              tesId && !tesIdsQueryResult.includes(tesId)
                ? [tesId, ...tesIdsQueryResult]
                : tesIdsQueryResult
            }
            isOptionEqualToValue={(option, selected) => option === selected}
            getOptionLabel={(value) => tes[value] || value}
            onInputChange={(event, newInput) => {
              if (event?.type === "change") {
                setTesSearchTerm(newInput);
              }
            }}
            onChange={(_, newTesId) => setTesId(newTesId)}
            renderInput={(params) => <TextField {...params} label={t("tes")} />}
          />
        </Grid>

        {tesId && (
          <>
            <Grid item xs={12}>
              <Typography color="text.secondary">
                {t("settings:tesSalaryTablesSelectedTes")}:{" "}
                {tes[tesId] || tesId}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ padding: 2 }}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  flexWrap="wrap"
                  gap={2}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={enabled}
                        onChange={(event) => setEnabled(event.target.checked)}
                      />
                    }
                    label={t("settings:tesSalaryTablesEnableForTes")}
                  />
                  <Box display="flex" gap={1}>
                    <Button
                      variant="contained"
                      disabled={isSavingConfig}
                      onClick={() => {
                        if (isSavingConfig) return;

                        setIsSavingConfig(true);
                        dispatch(
                          setTesSalaryTableConfigThunk({
                            tesId,
                            version: config?.version || 0,
                            enabled,
                          }),
                        ).then((action) => {
                          if (isFulfilled(action)) {
                            sendMessage(
                              t("SAVING_SUCCESS", { ns: "common" }),
                              "success",
                            );
                          }
                          reload();
                          setIsSavingConfig(false);
                        });
                      }}
                    >
                      {t("settings:tesSalaryTablesSaveConfig")}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => setIsSeriesDialogOpen(true)}
                    >
                      {t("settings:tesSalaryTablesCreateSeries")}
                    </Button>
                  </Box>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Tabs
                value={filterMode}
                onChange={(_, value: FilterMode) => setFilterMode(value)}
              >
                <Tab value="all" label={t("common:all")} />
                <Tab
                  value="published"
                  label={t("settings:tesSalaryTablesStatusPUBLISHED")}
                />
                <Tab
                  value="drafts"
                  label={t("settings:tesSalaryTablesStatusDRAFT")}
                />
                <Tab value="recent" label={t("hourEntries:modified")} />
              </Tabs>
            </Grid>

            {backendGap && (
              <Grid item xs={12}>
                <Alert severity="warning">{backendGap}</Alert>
              </Grid>
            )}

            {filteredGroups.map((group) => (
              <Grid item xs={12} key={group.series.tesSalaryTableSeriesId}>
                <TesSalaryTableSeriesCard
                  group={group}
                  localizedWorkTitles={localizedWorkTitles}
                  onOpenTable={openEditor}
                  onOpenSeriesEdit={openSeriesEditDialog}
                  onPublish={preparePublish}
                  onArchive={setTableToArchive}
                  onDelete={setTableToDelete}
                />
              </Grid>
            ))}
            {filteredGroups.length === 0 && (
              <Grid item xs={12}>
                <Alert severity="info">{t("common:noResults")}</Alert>
              </Grid>
            )}
          </>
        )}
      </Grid>

      <DialogComponent
        autoWidth
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
      >
        <TesSalaryTableEditorDialog
          mode={editorMode}
          open={editorOpen}
          onClose={() => setEditorOpen(false)}
          table={selectedTable}
          tesName={tesId ? tes[tesId] || tesId : ""}
          workTitleOptions={workTitleOptions}
          onSave={(content) => {
            if (!tesId) return;
            if (editorMode === "view") {
              setEditorOpen(false);
              return;
            }
            const sanitizedContent = sanitizeDraftContent(content);
            const createSeriesId =
              selectedSeriesId || selectedTable?.seriesId || null;
            const targetSeriesId =
              editorMode === "create"
                ? createSeriesId
                : selectedTable?.seriesId || null;
            const duplicateExists = hasDuplicateSeriesNameAndEffectiveDate({
              groupedTables,
              seriesId: targetSeriesId,
              name: sanitizedContent.name,
              effectiveDate: sanitizedContent.effectiveDate,
              excludedTableId:
                editorMode === "edit"
                  ? selectedTable?.tesSalaryTableId
                  : undefined,
            });
            if (duplicateExists) {
              sendMessage(
                t("settings:tesSalaryTablesDuplicateNameAndEffectiveDate"),
                "error",
              );
              return;
            }

            if (editorMode === "create") {
              if (!createSeriesId) return;

              return dispatch(
                createTesSalaryTableDraftThunk({
                  tesSalaryTableId: crypto.randomUUID(),
                  version: 0,
                  tesId,
                  seriesId: createSeriesId,
                  content: sanitizedContent,
                }),
              ).then(() => {
                setEditorOpen(false);
                reload();
              });
            }

            if (editorMode === "edit" && selectedTable) {
              return dispatch(
                replaceTesSalaryTableDraftThunk({
                  tesSalaryTableId: selectedTable.tesSalaryTableId,
                  version: selectedTable.version,
                  content: sanitizedContent,
                }),
              ).then(() => {
                setEditorOpen(false);
                reload();
              });
            }

            if (editorMode === "copy" && selectedTable) {
              return dispatch(
                copyTesSalaryTableToDraftThunk({
                  sourceTesSalaryTableId: selectedTable.tesSalaryTableId,
                  newTesSalaryTableId: crypto.randomUUID(),
                  contentOverride: sanitizedContent,
                }),
              ).then(() => {
                setEditorOpen(false);
                reload();
              });
            }
          }}
        />
      </DialogComponent>

      <TesSalaryTableSeriesDialog
        open={!!seriesToEdit}
        title={t("settings:tesSalaryTablesRenameSeries")}
        name={editedSeriesName}
        description={editedSeriesDescription}
        submitLabel={t("common:save")}
        submitting={isSavingSeriesEdit}
        onClose={closeSeriesEditDialog}
        onNameChange={setEditedSeriesName}
        onDescriptionChange={setEditedSeriesDescription}
        onSubmit={submitSeriesEdit}
      />

      <TesSalaryTableSeriesDialog
        open={isSeriesDialogOpen}
        title={t("settings:tesSalaryTablesCreateSeries")}
        name={newSeriesName}
        description={newSeriesDescription}
        submitLabel={t("common:create")}
        submitting={isCreatingSeries}
        onClose={closeCreateSeriesDialog}
        onNameChange={setNewSeriesName}
        onDescriptionChange={setNewSeriesDescription}
        onSubmit={submitCreateSeries}
      />

      <TesSalaryTablePublishDialog
        open={!!tableToPublish}
        isBusy={isPublishValidating || !!publishingTableId}
        targetTableLabel={getReadableTableLabel(
          tableToPublish?.name,
          tableToPublish?.effectiveDate,
        )}
        sourceTableLabel={getReadableTableLabel(
          tableToPublish?.sourceTableName,
          tableToPublish?.sourceTableEffectiveDate,
        )}
        publishesSalaryChanges={!!tableToPublish?.sourceTableId}
        canPublishWithoutSalaryChanges={tableToPublish?.status === "DRAFT"}
        publishEstimatedTotal={publishSelectedEstimatedTotal}
        publishEstimatedByType={publishEstimatedByType}
        publishProcessedTotal={publishProcessedTotal}
        publishSkippedTotal={publishSkippedTotal}
        publishProgressPercent={publishProgressPercent}
        publishPausedReason={publishPausedReason}
        publishWaitingSeconds={
          publishWaitingMs === null ? null : Math.ceil(publishWaitingMs / 1000)
        }
        publishProjectionCatchUp={publishProjectionCatchUp}
        isPublishing={!!publishingTableId}
        canContinue={
          publishProcessedTotal > 0 ||
          publishSkippedTotal > 0 ||
          !!publishCursor
        }
        publishSourceSelection={publishSourceSelection}
        onPublishSourceSelectionChange={setPublishSourceSelection}
        onClose={() => {
          if (publishingTableId) return;
          setTableToPublish(null);
          setPublishProcessedTotal(0);
          setPublishSkippedTotal(0);
          setPublishEstimatedTotal(null);
          setPublishEstimatedByType(null);
          setPublishCursor(null);
          setPublishVersion(null);
          setPublishPausedReason(null);
          setPublishWaitingMs(null);
          setPublishProjectionCatchUp(null);
        }}
        onConfirm={confirmPublish}
        onPause={() => {
          publishPauseRequestedRef.current = true;
          publishContinueNowRef.current?.();
          setPublishPausedReason(t("settings:tesSalaryTablesPublishPausing"));
        }}
        onContinueNow={() => publishContinueNowRef.current?.()}
        t={t}
      />

      <DialogComponent
        autoWidth
        open={!!tableToDelete}
        onClose={() => setTableToDelete(null)}
      >
        <Box sx={{ p: 3, minWidth: 420 }}>
          <Typography variant="h6" fontWeight={700} marginBottom={1}>
            {t("common:confirmDialogDelete")}
          </Typography>
          <Typography marginBottom={3}>
            {`${t("common:confirmDelete")} ${tableToDelete?.name || ""}`}
          </Typography>
          <Box display="flex" justifyContent="flex-end" gap={1}>
            <Button variant="outlined" onClick={() => setTableToDelete(null)}>
              {t("common:cancel")}
            </Button>
            <Button
              color="error"
              variant="contained"
              onClick={() => {
                if (!tableToDelete) return;
                dispatch(
                  deleteTesSalaryTableDraftThunk({
                    tesSalaryTableId: tableToDelete.tesSalaryTableId,
                    version: tableToDelete.version,
                  }),
                )
                  .unwrap()
                  .then(() => {
                    setTableToDelete(null);
                    reload();
                  });
              }}
            >
              {t("delete")}
            </Button>
          </Box>
        </Box>
      </DialogComponent>

      <DialogComponent
        autoWidth
        open={!!tableToArchive}
        onClose={() => setTableToArchive(null)}
      >
        <Box sx={{ p: 3, minWidth: 420 }}>
          <Typography variant="h6" fontWeight={700} marginBottom={1}>
            {t("settings:tesSalaryTablesArchive")}
          </Typography>
          <Typography marginBottom={3}>
            {`${t("common:confirm")} ${tableToArchive?.name || ""}`}
          </Typography>
          <Box display="flex" justifyContent="flex-end" gap={1}>
            <Button variant="outlined" onClick={() => setTableToArchive(null)}>
              {t("common:cancel")}
            </Button>
            <Button
              color="warning"
              variant="contained"
              onClick={() => {
                if (!tableToArchive) return;
                dispatch(
                  archiveTesSalaryTableThunk({
                    tesSalaryTableId: tableToArchive.tesSalaryTableId,
                    version: tableToArchive.version,
                  }),
                )
                  .unwrap()
                  .then(() => {
                    setTableToArchive(null);
                    reload();
                  });
              }}
            >
              {t("settings:tesSalaryTablesArchive")}
            </Button>
          </Box>
        </Box>
      </DialogComponent>
    </Box>
  );
};

export default TesSalaryTablesControlPanel;
